import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles, Tooltip, Typography } from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import { useMeContext } from '~/modules/me';
import {
  getReactMoneyFormatFromMe,
  getReactNumberFormatFromMe
} from '~/modules/common/numbers';
import OverviewItemTooltip from '~/modules/common/charts/dashboard/OverviewChart/OverviewItemTooltip';

const useStyles = makeStyles(theme => ({
  item: {
    display: 'flex',
    justifyContent: 'space-around',
    [theme.breakpoints.down('xs')]: {
      paddingTop: theme.spacing(2),
      justifyContent: 'center'
    }
  },
  container: {
    flexDirection: 'column',
    textAlign: 'center',
    [theme.breakpoints.down('xs')]: {
      minWidth: theme.spacing(17)
    }
  },
  acronym: {
    fontWeight: theme.typography.fontWeightMedium,
    fontFamily: theme.typography.subtitle1.fontFamily,
    color: theme.palette.text.secondary,
    textDecoration: 'underline dotted',
    cursor: 'help'
  },
  row: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  value: {
    margin: theme.spacing(0.5, 0),
    fontSize: theme.typography.h5.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary
  },
  notAvailable: {
    color: theme.palette.text.secondary,
    cursor: 'help'
  }
}));

const ProjectKPIItem = ({
  value,
  acronym,
  label,
  naTooltipMessage,
  scale,
  dateRange,
  dataQeId
}) => {
  const classes = useStyles();
  const { me } = useMeContext();

  const moneySettings = getReactMoneyFormatFromMe(me);
  const numberSettings = getReactNumberFormatFromMe(me);

  const renderValue = () => {
    if (value !== null) {
      return (
        <OverviewItemTooltip
          value={value}
          moneySettings={moneySettings}
          numberSettings={numberSettings}
          intlKey="cost"
          id="dashboardOverviewChart.costDescription"
          isPercentage
        />
      );
    }

    return (
      <Tooltip title={naTooltipMessage} arrow enterTouchDelay={0}>
        <span className={classNames(classes.value, classes.notAvailable)}>
          <FormattedMessage id="dashboardOverviewChart.na" />
        </span>
      </Tooltip>
    );
  };

  return (
    <div className={classes.item}>
      <div className={classes.container}>
        <Tooltip title={label} arrow enterTouchDelay={0}>
          <span className={classes.acronym} data-qe-id={dataQeId}>
            {acronym}
          </span>
        </Tooltip>
        <div className={classes.row}>
          <Typography className={classes.value} data-qe-id={dataQeId}>
            {renderValue()}
          </Typography>
        </div>
      </div>
    </div>
  );
};

ProjectKPIItem.propTypes = {
  value: PropTypes.number,
  acronym: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  naTooltipMessage: PropTypes.string.isRequired,
  scale: PropTypes.string,
  dateRange: PropTypes.object,
  dataQeId: PropTypes.string
};

export default ProjectKPIItem;
